import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { currenciesAPI } from '../../services/api';
import { Currency, CreateCurrencyDto, UpdateCurrencyDto } from '../../types';
import {
  ArrowLeftIcon,
  CurrencyDollarIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { toastSuccess } from '../../utils/toast';
import { useTranslation } from 'react-i18next';

const CurrencyForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<CreateCurrencyDto>({
    code: '',
    name: '',
    symbol: '',
    exchangeRate: 1.0,
    isBase: false,
    isActive: true,
  });

  useEffect(() => {
    if (id) {
      loadFormData();
    }
  }, [id]);

  const loadFormData = async () => {
    if (!id) return;

    try {
      setLoading(true);
      const currency = await currenciesAPI.getById(id);
      setFormData({
        code: currency.code,
        name: currency.name,
        symbol: currency.symbol || '',
        exchangeRate: typeof currency.exchangeRate === 'string' ? parseFloat(currency.exchangeRate) : currency.exchangeRate,
        isBase: currency.isBase,
        isActive: currency.isActive,
      });
    } catch (err) {
      setError(t('currencies.errors.load'));
      console.error('Error loading currency:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
              type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError(null);

      if (id) {
        // Update existing currency
        const updateData: UpdateCurrencyDto = {
          code: formData.code,
          name: formData.name,
          symbol: formData.symbol,
          exchangeRate: formData.exchangeRate,
          isBase: formData.isBase,
          isActive: formData.isActive,
        };
        await currenciesAPI.update(id, updateData);
      } else {
        // Create new currency
        await currenciesAPI.create(formData);
      }
      toastSuccess(id ? t('toast.updated', 'Updated successfully') : t('toast.created', 'Created successfully'));
      navigate('/currencies');
    } catch (err) {
      setError(t('currencies.errors.save'));
      console.error('Error saving currency:', err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/currencies')}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-white rounded-lg transition-all duration-200"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{id ? t('currencies.form.headerEdit') : t('currencies.form.headerCreate')}</h1>
            <p className="text-gray-600">{t('currencies.form.subtitle')}</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="text-red-800">{error}</div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
              <CurrencyDollarIcon className="h-5 w-5 mr-2" />
              {t('currencies.form.sections.basic')}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('currencies.form.fields.currencyCode')}</label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleInputChange}
                  required
                  maxLength={3}
                  placeholder={t('currencies.form.placeholders.code')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-sm text-gray-500">{t('currencies.form.hints.iso3')}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('currencies.form.fields.currencyName')}</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  placeholder={t('currencies.form.placeholders.name')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('currencies.form.fields.currencySymbol')}</label>
                <input
                  type="text"
                  name="symbol"
                  value={formData.symbol}
                  onChange={handleInputChange}
                  placeholder={t('currencies.form.placeholders.symbol')}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-sm text-gray-500">{t('currencies.form.hints.optionalSymbol')}</p>
              </div>
            </div>
          </div>

          {/* Exchange Rate and Settings */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('currencies.form.sections.exchange')}</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('currencies.form.fields.exchangeRate')}</label>
                <input
                  type="number"
                  step="0.0001"
                  min="0"
                  name="exchangeRate"
                  value={formData.exchangeRate}
                  onChange={handleInputChange}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                />
                <p className="mt-1 text-sm text-gray-500">{t('currencies.form.hints.exchangeRelative')}</p>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isBase"
                  checked={formData.isBase}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 flex items-center">
                  <StarIcon className="h-4 w-4 text-yellow-500 mr-1" />
                  {t('currencies.form.fields.setBase')}
                </label>
              </div>
              <p className="text-sm text-gray-500">{t('currencies.form.hints.baseHasOne')}</p>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  {t('currencies.form.fields.activeCurrency')}
                </label>
              </div>
              <p className="text-sm text-gray-500">{t('currencies.form.hints.inactiveWarning')}</p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/currencies')}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {t('currencies.form.actions.cancel')}
          </button>
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {saving ? t('currencies.form.actions.saving') : id ? t('currencies.form.actions.update') : t('currencies.form.actions.create')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CurrencyForm;
